import {useEffect, useId, useMemo, useState} from 'react';
import {Lock, X} from 'lucide-react';
import {PhoneHQButton, PhoneHQInput, Segmented} from 'ch3-ui-lib';
import type {
    CallRestrictionRule,
    CallRestrictions,
    DestinationType,
    RuleAction,
    RuleInput,
    RulePurpose,
    ScopeId,
} from '../../store/features/CallRestrictions/callRestrictionsTypes';
import {
    countryName,
    DESTINATION_TYPE_LABEL,
    describeDestination,
    destinationFromDraft,
    DestinationDraft,
    draftOf,
    EMPTY_DRAFT,
    overlappingPrefixes,
    prefixesOf,
    prefixHint,
} from '../../utils/callRestrictions/destinations';
import {getCallingCountries} from '../../components/Dialer/services/countryDataService';
import {LabeledSelect, lucide} from './parts';
import {ScopeKind, scopeKindOf, ScopeOption} from './scopes';

interface RulePanelProps {
    data: CallRestrictions;
    /** The rule being edited; undefined adds a new one. */
    rule?: CallRestrictionRule;
    /** Scope to start with when adding, e.g. from a group's row. */
    initialScope?: ScopeId;
    groups: ScopeOption[];
    people: ScopeOption[];
    numbers?: ScopeOption[];
    saving: boolean;
    error?: string;
    onSubmit: (input: RuleInput) => void;
    onClose: () => void;
}

const TYPES: DestinationType[] = ['COUNTRY', 'PREFIX', 'NUMBER', 'RANGE', 'CATEGORY'];

const possessive = (name: string) => (name.endsWith('s') ? `${name}’` : `${name}’s`);

/** Board AdminRule: a 640px form panel over the page. */
export const RulePanel = ({data, rule, initialScope, groups, people, numbers = [], saving, error, onSubmit, onClose}: RulePanelProps) => {
    const titleId = useId();
    const startScope = rule?.scopeId ?? initialScope ?? 'company';
    const [kind, setKind] = useState<ScopeKind>(scopeKindOf(startScope));
    // `group-` / `user-` alone (from "Add group or person") picks the kind but not the scope.
    const [scopeId, setScopeId] = useState<ScopeId>(/^(group|user|number)-$/.test(startScope) ? '' : startScope);
    const [action, setAction] = useState<RuleAction>(rule?.action ?? 'BLOCK');
    const [draft, setDraft] = useState<DestinationDraft>(rule ? draftOf(rule.destination) : EMPTY_DRAFT);
    const [appliesTo, setAppliesTo] = useState<RulePurpose[]>(rule?.appliesTo ?? ['CALL', 'FORWARD']);
    const [note, setNote] = useState(rule?.note ?? '');
    const [validation, setValidation] = useState<string>();
    const countries = useMemo(
        () => getCallingCountries().map((c) => ({iso: c.alpha2, name: countryName(c.alpha2), code: c.countryCallingCodes?.[0] ?? ''}))
            .sort((a, b) => a.name.localeCompare(b.name)),
        [],
    );

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const options = kind === 'group' ? groups : kind === 'person' ? people : kind === 'number' ? numbers : [];
    const scope: ScopeOption | undefined = kind === 'company'
        ? {scopeId: 'company', name: 'Whole company', mode: data.company.mode}
        : options.find((o) => o.scopeId === scopeId);
    const scopeName = kind === 'company' ? 'the company' : scope?.name ?? '';

    const changeKind = (next: ScopeKind) => {
        setKind(next);
        setScopeId(next === 'company' ? 'company' : '');
    };

    const setField = (field: keyof DestinationDraft, value: string) => {
        setDraft((current) => ({...current, [field]: value}));
        setValidation(undefined);
    };

    const togglePurpose = (purpose: RulePurpose) => {
        setAppliesTo((current) => current.includes(purpose) ? current.filter((p) => p !== purpose) : [...current, purpose]);
        setValidation(undefined);
    };

    const parsed = destinationFromDraft(draft);
    const blockedByPhoneHq = useMemo(() => {
        if (action !== 'ALLOW' || !parsed.destination) return [];
        return data.phonehq
            .filter((entry) => entry.action === 'BLOCK')
            .filter((entry) => overlappingPrefixes(parsed.destination!, prefixesOf(entry.destination, data.categories)).length > 0);
    }, [action, parsed.destination, data.phonehq, data.categories]);

    const submit = () => {
        if (kind !== 'company' && !scopeId) {
            setValidation(kind === 'group' ? 'Choose a group.' : kind === 'number' ? 'Choose a number.' : 'Choose a person.');
            return;
        }
        if (parsed.error) {
            setValidation(parsed.error);
            return;
        }
        if (appliesTo.length === 0) {
            setValidation('Choose outgoing calls, forwarding or both.');
            return;
        }
        onSubmit({
            scopeId,
            action,
            destination: parsed.destination!,
            appliesTo: (['CALL', 'FORWARD'] as RulePurpose[]).filter((p) => appliesTo.includes(p)),
            ...(note.trim() ? {note: note.trim()} : {}),
        });
    };

    const allowHint = scope?.mode === 'ALLOW_LIST' && scopeName
        ? `Add to ${possessive(scope.name === 'Whole company' ? 'the company' : scope.name)} allow list`
        : 'Allow even if a broader rule blocks it';
    const blockHint = scope?.mode === 'BLOCK_LIST' && scopeName && kind !== 'company'
        ? `Add to ${possessive(scope.name)} block list`
        : 'Block even if the list would allow it';

    const radioCard = (value: RuleAction, label: string, hint: string) => {
        const selected = action === value;
        return (
            <label className={`flex cursor-pointer items-start gap-2.5 rounded-control p-3 ${selected ? 'border-2 border-solid border-brand bg-brand-tint-subtle' : 'border border-solid border-line'}`}>
                <input type="radio" name="rule-action" checked={selected} onChange={() => setAction(value)} className="mt-0.5 size-4 accent-brand"/>
                <span className="flex flex-col gap-0.5">
                    <span className="text-callout font-semibold text-ink">{label}</span>
                    <span className="text-footnote text-ink-muted">{hint}</span>
                </span>
            </label>
        );
    };

    const checkbox = (purpose: RulePurpose, label: string, hint: string) => (
        <label className="flex cursor-pointer items-start gap-2.5">
            <input type="checkbox" checked={appliesTo.includes(purpose)} onChange={() => togglePurpose(purpose)} className="mt-0.5 size-4 accent-brand"/>
            <span className="flex flex-col gap-0.5">
                <span className="text-callout text-ink">{label}</span>
                <span className="text-footnote text-ink-muted">{hint}</span>
            </span>
        </label>
    );

    const ownPrefix = parsed.destination ? prefixesOf(parsed.destination, data.categories)[0] : undefined;

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-scrim" onClick={onClose} data-testid="rule-panel-scrim"/>
            <section role="dialog" aria-modal="true" aria-labelledby={titleId} className="relative flex h-full w-[640px] max-w-full flex-col bg-surface shadow-overlay">
                <header className="flex h-header shrink-0 items-center justify-between border-0 border-b border-solid border-line pl-6 pr-4">
                    <h1 id={titleId} className="m-0 text-title text-ink">{rule ? 'Edit rule' : 'Add rule'}</h1>
                    <button type="button" aria-label="Close" onClick={onClose}
                            className="flex size-9 cursor-pointer items-center justify-center rounded-control text-ink-secondary hover:bg-surface-muted">
                        <X size={20} aria-hidden="true" className={lucide}/>
                    </button>
                </header>

                <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-6">
                    <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
                        <legend className="mb-2 p-0 text-caption text-ink-muted">Who it applies to</legend>
                        <Segmented<ScopeKind>
                            label="Who it applies to"
                            value={kind}
                            onChange={changeKind}
                            options={[{id: 'company', label: 'Whole company'}, {id: 'group', label: 'Group'}, {id: 'person', label: 'Person'}, {id: 'number', label: 'Number'}]}
                        />
                        {kind !== 'company' && (
                            <LabeledSelect label={kind === 'group' ? 'Group' : kind === 'number' ? 'Number' : 'Person'} value={scopeId} onChange={(e) => {
                                setScopeId(e.target.value);
                                setValidation(undefined);
                            }}>
                                <option value="">{kind === 'group' ? 'Choose a group' : kind === 'number' ? 'Choose a number' : 'Choose a person'}</option>
                                {options.map((o) => (
                                    <option key={o.scopeId} value={o.scopeId}>{o.sub ? `${o.name} · ${o.sub}` : o.name}</option>
                                ))}
                            </LabeledSelect>
                        )}
                    </fieldset>

                    <fieldset className="m-0 border-0 p-0">
                        <legend className="mb-2 p-0 text-caption text-ink-muted">Rule</legend>
                        <div className="grid grid-cols-2 gap-2">
                            {radioCard('ALLOW', 'Allow', allowHint)}
                            {radioCard('BLOCK', 'Block', blockHint)}
                        </div>
                    </fieldset>

                    <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
                        <legend className="mb-2 p-0 text-caption text-ink-muted">Destination</legend>
                        <div role="radiogroup" aria-label="Destination type" className="flex flex-wrap gap-2">
                            {TYPES.filter((type) => type !== 'CATEGORY' || data.categories.length > 0 || draft.type === 'CATEGORY').map((type) => (
                                <button
                                    key={type}
                                    type="button"
                                    role="radio"
                                    aria-checked={draft.type === type}
                                    onClick={() => setField('type', type)}
                                    className={`h-8 cursor-pointer rounded-chip border-0 px-3 text-footnote font-medium ${draft.type === type ? 'bg-brand-tint text-brand-ink' : 'bg-surface-muted text-ink-secondary hover:bg-line'}`}
                                >
                                    {DESTINATION_TYPE_LABEL[type]}
                                </button>
                            ))}
                        </div>
                        {draft.type === 'COUNTRY' && (
                            <LabeledSelect label="Country" value={draft.country} onChange={(e) => setField('country', e.target.value)}>
                                <option value="">Choose a country</option>
                                {countries.map((c) => <option key={c.iso} value={c.iso}>{`${c.name} (${c.code})`}</option>)}
                            </LabeledSelect>
                        )}
                        {draft.type === 'PREFIX' && (
                            <>
                                <PhoneHQInput label="Numbers starting with" inputMode="tel" placeholder="+44" value={draft.prefix}
                                              onChange={(e) => setField('prefix', e.target.value)}/>
                                <p className="m-0 text-footnote text-ink-muted">{prefixHint(draft.prefix)}</p>
                            </>
                        )}
                        {draft.type === 'NUMBER' && (
                            <>
                                <PhoneHQInput label="Number" inputMode="tel" placeholder="+48 22 555 01 99" value={draft.number}
                                              onChange={(e) => setField('number', e.target.value)}/>
                                <p className="m-0 text-footnote text-ink-muted">Use the international format, starting with +.</p>
                            </>
                        )}
                        {draft.type === 'RANGE' && (
                            <>
                                <div className="grid grid-cols-2 gap-2">
                                    <PhoneHQInput label="From" inputMode="tel" placeholder="+48 22 555 01 00" value={draft.rangeFrom}
                                                  onChange={(e) => setField('rangeFrom', e.target.value)}/>
                                    <PhoneHQInput label="To" inputMode="tel" placeholder="+48 22 555 01 99" value={draft.rangeTo}
                                                  onChange={(e) => setField('rangeTo', e.target.value)}/>
                                </div>
                                <p className="m-0 text-footnote text-ink-muted">Both numbers need the same number of digits. The range includes them.</p>
                            </>
                        )}
                        {draft.type === 'CATEGORY' && (
                            <LabeledSelect label="Category" value={draft.category} onChange={(e) => setField('category', e.target.value)}>
                                <option value="">Choose a category</option>
                                {draft.category && !data.categories.some((c) => c.id === draft.category) && (
                                    <option value={draft.category}>{draft.category}</option>
                                )}
                                {data.categories.map((c) => (
                                    <option key={c.id} value={c.id}>{c.prefixes.length ? `${c.name} (${c.prefixes.map((p) => `+${p}`).join(', ')})` : c.name}</option>
                                ))}
                            </LabeledSelect>
                        )}
                    </fieldset>

                    <fieldset className="m-0 flex flex-col gap-2.5 border-0 p-0">
                        <legend className="mb-2 p-0 text-caption text-ink-muted">Applies to</legend>
                        {checkbox('CALL', 'Outgoing calls', 'From the apps, desk phones and click-to-call')}
                        {checkbox('FORWARD', 'Forwarding', 'Personal forwarding, line forwarding, and queue and menu destinations')}
                    </fieldset>

                    <PhoneHQInput label="Note (optional)" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)}/>

                    {blockedByPhoneHq.map((entry) => {
                        const text = describeDestination(entry.destination, data.categories, entry.destinationLabel || undefined);
                        const prefix = text.detail.split(' · ')[1];
                        return (
                            <div key={entry.id} role="note" className="flex gap-2.5 rounded-control bg-warning-tint p-3 text-footnote text-warning-ink">
                                <Lock size={16} aria-hidden="true" className={`${lucide} mt-px`}/>
                                <span>
                                    <b className="font-semibold">{text.name}{prefix && !text.name.includes(prefix) ? ` (${prefix})` : ''} stay blocked.</b>{' '}
                                    PhoneHQ blocks them for your company, so this rule allows the rest{ownPrefix ? ` of +${ownPrefix}` : ''}.
                                </span>
                            </div>
                        );
                    })}

                    {(validation || error) && (
                        <p role="alert" className="m-0 text-footnote text-danger-ink">{validation ?? error}</p>
                    )}
                </div>

                <footer className="flex justify-end gap-2 border-0 border-t border-solid border-line px-6 py-3">
                    <PhoneHQButton variant="tertiary" onClick={onClose}>Cancel</PhoneHQButton>
                    <PhoneHQButton onClick={submit} disabled={saving} data-testid="rule-panel-submit">
                        {rule ? 'Save changes' : 'Add rule'}
                    </PhoneHQButton>
                </footer>
            </section>
        </div>
    );
};
